import type { Message } from "@ag-ui/client";
import type { Agent } from "~/services/agents";
import type { ProjectConversation } from "~/services/projects";

// The user's selected next-turn agent for a conversation — the composer's active
// agent, advanced by an explicit switch, by accepting a handoff suggestion, or by
// the agent picked at creation (so the first turn, which has no turns yet to
// derive a current agent from, still resolves one). Persisted in sessionStorage
// so the selection survives a refresh, then clears when the tab closes: this is
// transient next-turn intent, not a durable preference, so a much-later visit
// re-surfaces a pending suggestion fresh.
const nextTurnAgentStorageKey = (conversationId: string) => `next-turn-agent:${conversationId}`;

export const readNextTurnAgent = (conversationId: string): string | null => {
  try {
    return sessionStorage.getItem(nextTurnAgentStorageKey(conversationId));
  } catch {
    return null;
  }
};

export const writeNextTurnAgent = (conversationId: string, agentId: string) => {
  try {
    sessionStorage.setItem(nextTurnAgentStorageKey(conversationId), agentId);
  } catch {
    // Storage unavailable (e.g. private browsing) — the selection just won't
    // survive a refresh.
  }
};

// A turn's opening user message as /connect serves it: the agent that handled
// the turn rides its `agentId` extra, absent on a turn recorded without one.
function namesTurnAgent(message: Message): message is Message & { agentId: string } {
  return message.role === "user" && "agentId" in message && typeof message.agentId === "string";
}

// The agent that handled the conversation's latest turn — the next-turn agent
// when no selection is saved. A turn without an agent is skipped, as the server
// skips it for the conversation's current agent. Null when no turn names one,
// e.g. before the first turn.
export function latestTurnAgentId(messages: readonly Message[]): string | null {
  return messages.findLast(namesTurnAgent)?.agentId ?? null;
}

// The project's default agent, for a session with no picked agent and no turn
// that names one: the agent of the latest session that has one, if still usable,
// else the first usable agent. The New Session splash preselects it, and an empty
// session falls back to it. Null when no agent is usable.
export function defaultSessionAgentId(
  usableAgents: readonly Pick<Agent, "id">[],
  sessions: readonly Pick<ProjectConversation, "agent">[]
): string | null {
  const lastUsedAgentId = sessions.find((s) => s.agent?.id)?.agent?.id;
  return usableAgents.find((a) => a.id === lastUsedAgentId)?.id ?? usableAgents[0]?.id ?? null;
}
